import React, { useState } from "react";
import { Heart } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { AvatarDisplay } from "./AvatarDisplay";
import { formatTimeAgo, formatCount } from "../../../utils/formatters";

const CommentReply = React.memo(
  ({
    reply,
    onLikeToggle,
    onShowActionMenu,
    activeCommentId
  }) => {
    const navigate = useNavigate();
    const [isLiked, setIsLiked] = useState(reply.isLikedByUser || false);
    const [likesCount, setLikesCount] = useState(reply.stats?.likes || 0);

    const isActive = activeCommentId === reply._id;

    const handleLike = async () => {
      const newLikedState = !isLiked;
      const previousLikesCount = likesCount;

      // Optimistic update
      setIsLiked(newLikedState);
      setLikesCount((prev) => (newLikedState ? prev + 1 : prev - 1));

      try {
        await onLikeToggle(reply._id, newLikedState);
      } catch (error) {
        // Revert on error
        setIsLiked(!newLikedState);
        setLikesCount(previousLikesCount);
      }
    };

    // Helper function to get profile picture
    const getProfilePicture = () => {
      const profilePic = reply.author?.profilePicture;

      if (profilePic && typeof profilePic === "object") {
        return profilePic.thumbnail || profilePic.small || profilePic.medium;
      }

      if (typeof profilePic === "string") {
        return profilePic;
      }

      return null;
    };

    const profilePictureUrl = getProfilePicture();

    return (
      <div
        className={`space-y-2 rounded-lg transition-all duration-300 ${isActive
            ? "bg-white rounded-xl p-4 shadow-2xl z-20 relative scale-[1.02]"
            : ""
          }`}
      >
        {/* First Row: Profile picture and username in same line */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 flex-shrink-0 rounded-full overflow-hidden">
              <AvatarDisplay
                profilePictureUrl={profilePictureUrl}
                name={reply.author?.username}
              />
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => navigate(`/${reply.author?.username}`)}
                className="font-semibold text-sm text-gray-900 cursor-pointer"
              >
                {reply.author?.username}
              </button>
              <span className="text-xs text-gray-400">
                {formatTimeAgo(reply.createdAt)}
              </span>
            </div>
          </div>

          <button
            onClick={() => onShowActionMenu(isActive ? null : reply._id)}
            className="p-1 rounded-full hover:bg-gray-200 transition-colors"
          >
            <svg width="24" height="24" viewBox="0 0 24 24">
              <line
                x1="8"
                y1="9"
                x2="16"
                y2="9"
                stroke="#6b7280"
                strokeWidth="2.2"
                strokeLinecap="round"
              />
              <line
                x1="4"
                y1="15"
                x2="20"
                y2="15"
                stroke="#6b7280"
                strokeWidth="2.2"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        {/* Second Row: Comment text */}
        <div className="flex gap-1">
          <div className="flex-shrink-0" />

          <div className="flex-1 min-w-0">
            <p className="text-sm text-black leading-relaxed break-words mb-2">
              {reply.content}
            </p>

            {/* Like button only - no Reply button */}
            <div className="flex items-center">
              <button
                onClick={handleLike}
                className={`flex items-center gap-1.5 py-1.5 rounded-full text-xs font-medium transition-colors ${isLiked ? "text-red-500" : "text-gray-900"
                  }`}
              >
                <Heart
                  size={14}
                  className={`transition-all ${isLiked ? "fill-red-500 animate-heart-pop" : ""
                    }`}
                />
                {likesCount > 0 && <span>{formatCount(likesCount)}</span>}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  },
  (prevProps, nextProps) => {
    return (
      prevProps.reply._id === nextProps.reply._id &&
      prevProps.reply.stats?.likes === nextProps.reply.stats?.likes &&
      prevProps.reply.isLikedByUser === nextProps.reply.isLikedByUser &&
      prevProps.currentUserProfileId === nextProps.currentUserProfileId &&
      prevProps.activeCommentId === nextProps.activeCommentId
    );
  }
);

CommentReply.displayName = "CommentReply";

export default CommentReply;